/**
 * Czytelne nazwy w dzienniku audytu (`aud.labels.*`): encje (nazwy kontrolerów z automatycznych wpisów i typy z wpisów
 * jawnych), klucze metadanych i znane wartości. Brak wpisu nie jest błędem — `auditFormat` rozbija wtedy nazwę techniczną
 * na słowa. Wersja angielska: `audit-labels-en.ts`, typowana względem tej.
 */
export type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const audLabelsPl = {
  entities: {
    // Automatyczne wpisy (nazwa kontrolera bez „Controller”)
    Ai: 'Asystent AI',
    Alerts: 'Alerty',
    Analytics: 'Analityka',
    AnalyticsCustomers: 'Analityka — klienci',
    AnalyticsFinance: 'Analityka — finanse',
    AnalyticsOps: 'Analityka — operacje',
    AnalyticsProducts: 'Analityka — produkty',
    Attachments: 'Załączniki',
    Audit: 'Dziennik audytu',
    Auth: 'Logowanie i konto',
    Cashflow: 'Przepływy pieniężne',
    Chat: 'Czat',
    ChatStream: 'Czat',
    Checklists: 'Listy kontrolne',
    Claims: 'Szkody',
    ClaimsDocuments: 'Szkody — dokumenty',
    ClaimsQueue: 'Szkody — kolejka',
    Collections: 'Windykacja',
    Commissions: 'Prowizje',
    CompetitorSuppliers: 'Dostawcy konkurencji',
    CostDepartments: 'Działy kosztowe',
    Costs: 'Koszty',
    CustomerContacts: 'Kontakty klienta',
    CustomerDocuments: 'Dokumenty klienta',
    CustomerLifecycle: 'Cykl życia klienta',
    CustomerProfile: 'Profil klienta',
    Customers: 'Klienci',
    DailyReport: 'Raport dzienny',
    DailyReportSheet: 'Arkusz raportu dziennego',
    Demo: 'Tryb demo',
    ErpOutboxAdmin: 'Kolejka do ERP — administracja',
    ErpOutboxIntegration: 'Kolejka do ERP',
    ErpSync: 'Synchronizacja z ERP',
    Exports: 'Eksporty',
    Field: 'Teren',
    FieldForeign: 'Teren — klienci innych handlowców',
    FieldVisits: 'Teren — wizyty',
    FleetAnalytics: 'Flota — analityka',
    FleetAttachments: 'Flota — załączniki',
    FleetInsights: 'Flota — wnioski',
    FleetInsightsUi: 'Flota — wnioski',
    FleetRouteSync: 'Flota — synchronizacja tras',
    FuelExpenses: 'Paliwo i wydatki',
    Gdpr: 'Dane osobowe (RODO)',
    Geocoding: 'Geokodowanie adresów',
    Incidents: 'Zdarzenia floty',
    InsuranceAnalytics: 'Ubezpieczenia — analityka',
    InsuranceContracts: 'Ubezpieczenia — umowy',
    InsuranceCoverage: 'Ubezpieczenia — zakres',
    InsuranceImports: 'Ubezpieczenia — import',
    InsurancePolicies: 'Polisy',
    InvoiceCompare: 'Porównanie faktur',
    Leads: 'Leady',
    Leases: 'Leasing',
    Leave: 'Urlopy',
    Logbooks: 'Karty drogowe',
    Logistics: 'Logistyka',
    LogisticsOptimization: 'Logistyka — optymalizacja',
    LogisticsReports: 'Logistyka — raporty',
    MbrClose: 'Zamknięcie miesiąca',
    MbrCloseForms: 'Zamknięcie miesiąca — formularze',
    MbrCloseImport: 'Zamknięcie miesiąca — import',
    MbrCommon: 'Raport zarządu',
    MbrComplaints: 'Raport zarządu — reklamacje',
    MbrCustomers: 'Raport zarządu — klienci',
    MbrDisposals: 'Raport zarządu — utylizacja',
    MbrFinance: 'Raport zarządu — finanse',
    MbrInsights: 'Raport zarządu — wnioski',
    MbrLogistics: 'Raport zarządu — logistyka',
    MbrOperations: 'Raport zarządu — operacje',
    MbrReport: 'Raport zarządu',
    MbrSales: 'Raport zarządu — sprzedaż',
    MyDay: 'Mój dzień',
    MySummary: 'Moje podsumowanie',
    Notifications: 'Powiadomienia',
    NotificationsAnnounce: 'Ogłoszenia',
    Organization: 'Organizacja',
    OwnerDashboard: 'Pulpit właściciela',
    Planner: 'Planer',
    PlannerOrgSettings: 'Planer — ustawienia',
    PlannerPeople: 'Planer — pracownicy',
    PlannerSetup: 'Planer — konfiguracja',
    PlannerVacation: 'Planer — urlopy',
    Prices: 'Cenniki',
    Purchasing: 'Zakupy',
    Push: 'Powiadomienia push',
    Quality: 'Jakość',
    QualityEvents: 'Jakość — zdarzenia',
    RepSales: 'Sprzedaż handlowców',
    RouteOptions: 'Opcje tras',
    Routing: 'Trasy',
    SalesOrders: 'Zamówienia',
    Search: 'Wyszukiwanie',
    Surveys: 'Ankiety',
    Tasks: 'Zadania',
    TenderAttachments: 'Przetargi — załączniki',
    Tenders: 'Przetargi',
    Users: 'Użytkownicy',
    Vehicles: 'Pojazdy',
    Warehouse: 'Magazyn',
    WarehouseAdmin: 'Magazyn — ustawienia',
    Workflow: 'Akceptacje',
    Worktime: 'Czas pracy',
    WorktimeAdmin: 'Czas pracy — ustawienia',
    // Wpisy jawne
    Attachment: 'Załącznik',
    AuditLog: 'Dziennik audytu',
    CUSTOMER_DOCUMENT_SEND: 'Wysyłka dokumentu do klienta',
    CashBankBalance: 'Saldo kasy i banku',
    Contact: 'Kontakt',
    Customer: 'Klient',
    CustomerDocumentSend: 'Wysyłka dokumentu do klienta',
    CustomerMenuLink: 'Link do menu klienta',
    CustomerRetro: 'Retro u klienta',
    Delivery: 'Dostawa',
    DriverGroup: 'Grupa kierowców',
    EXPENSE: 'Wydatek',
    ErpEmployee: 'Pracownik z ERP',
    Export: 'Eksport',
    FLEET_CHECKLIST_ANSWER: 'Odpowiedź z listy kontrolnej',
    FLEET_FUEL: 'Tankowanie',
    FUEL: 'Tankowanie',
    FleetChecklistRun: 'Wypełniona lista kontrolna',
    FleetChecklistTemplate: 'Szablon listy kontrolnej',
    FleetExpense: 'Wydatek floty',
    FleetExternalIdMap: 'Powiązanie z systemem zewnętrznym',
    FleetFuelEntry: 'Tankowanie',
    FleetIncident: 'Zdarzenie floty',
    FleetIncidentTemplate: 'Szablon zdarzenia floty',
    FleetRouteSyncRun: 'Synchronizacja tras',
    FleetServiceRecord: 'Serwis pojazdu',
    FleetSettings: 'Ustawienia floty',
    ForeignCustomerBrowse: 'Klienci innych handlowców',
    ForeignCustomerLookup: 'Klient innego handlowca w pobliżu',
    ForeignCustomerView: 'Karta klienta innego handlowca',
    INCIDENT: 'Zdarzenie floty',
    INSURANCE_CLAIM: 'Szkoda',
    LEASE: 'Leasing',
    LOGBOOK: 'Karta drogowa',
    LeaseContract: 'Umowa leasingu',
    Logbook: 'Karta drogowa',
    MbrImport: 'Import raportu zarządu',
    MbrMonthClose: 'Zamknięcie miesiąca',
    OdometerReading: 'Odczyt licznika',
    OrganizationPlannerSettings: 'Ustawienia planera',
    OrganizationSettings: 'Ustawienia organizacji',
    PlannerShift: 'Zmiana w grafiku',
    PlannerTaskTemplate: 'Szablon zadania',
    RepSalesBatch: 'Paczka sprzedaży handlowca',
    RepSalesReminder: 'Przypomnienie o sprzedaży',
    ScheduleRequest: 'Prośba o zmianę grafiku',
    SettlementPeriod: 'Okres rozliczeniowy',
    ShiftAttributeDef: 'Atrybut zmiany',
    SurveyTemplate: 'Szablon ankiety',
    TimeEntry: 'Wpis czasu pracy',
    User: 'Użytkownik',
    VEHICLE: 'Pojazd',
    VacationEntitlement: 'Wymiar urlopu',
    Vehicle: 'Pojazd',
    VehicleAssignment: 'Przydział pojazdu',
    Visit: 'Wizyta',
    WorkTime: 'Czas pracy',
    WorkTimeProfile: 'Profil czasu pracy',
    WorkingTimeRuleset: 'Zasady czasu pracy',
  },
  keys: {
    zdarzenie: 'Zdarzenie',
    operacja: 'Operacja',
    numer: 'Numer dokumentu',
    odbiorca: 'Odbiorca',
    kanal: 'Kanał',
    decyzja: 'Decyzja',
    odmowa: 'Odmowa dostępu',
    reason: 'Powód',
    email: 'E-mail',
    employeeId: 'Nr ewidencyjny',
    role: 'Rola',
    target: 'Cel',
    format: 'Format',
    count: 'Liczba rekordów',
    rows: 'Liczba wierszy',
    scope: 'Zakres',
    method: 'Metoda',
    path: 'Ścieżka',
    source: 'Źródło',
    kind: 'Rodzaj zdarzenia',
    page: 'Strona listy',
    search: 'Wyszukiwana fraza',
    customerIds: 'Klienci na stronie',
    customerId: 'Klient',
    foreignTotal: 'Wszystkich pasujących',
    ownerId: 'Opiekun klienta',
    userId: 'Użytkownik',
    fromUserId: 'Od użytkownika',
    toUserId: 'Do użytkownika',
    nextTaskAssigneeId: 'Wykonawca kolejnego zadania',
    visitId: 'Wizyta',
    vehicleId: 'Pojazd',
    subjectId: 'Osoba',
    truncated: 'Lista obcięta',
    visibility: 'Widoczność',
    radiusM: 'Promień (m)',
    distanceM: 'Odległość (m)',
    lat: 'Szerokość geogr.',
    lng: 'Długość geogr.',
    handler: 'Operacja w systemie',
    filters: 'Filtry',
    status: 'Status',
    period: 'Okres',
    date: 'Data',
    from: 'Od',
    to: 'Do',
    note: 'Notatka',
    query: 'Zapytanie',
    trigger: 'Wyzwalacz',
    dryRun: 'Próba bez zapisu',
    sent: 'Wysłano',
    failed: 'Nieudane',
    recipients: 'Odbiorcy',
    attachments: 'Załączniki',
    bytes: 'Rozmiar (bajty)',
    op: 'Operacja',
    event: 'Zdarzenie',
    action: 'Akcja',
  },
  values: {
    kind: {
      FOREIGN_CUSTOMER_BROWSE: 'Przeglądanie listy klientów innych handlowców',
      FOREIGN_CUSTOMER_GEO: 'Klienci innych handlowców na mapie',
      FOREIGN_CUSTOMER_VIEW: 'Otwarcie karty klienta innego handlowca',
      FOREIGN_CUSTOMER_LOOKUP: 'Klient innego handlowca w pobliżu',
    },
    scope: { foreign: 'Klienci innych handlowców', mine: 'Moi klienci', all: 'Wszyscy klienci' },
    visibility: { LIMITED: 'Ograniczona', NONE: 'Brak' },
  },
  view: 'Wgląd w dane',
  more: '+{count} więcej',
  openCustomer: 'Otwórz kartę klienta',
  fullId: 'Identyfikator: {id}',
};

export { audLabelsEn } from './audit-labels-en';
